import { A, useParams } from '@solidjs/router';
import { createContext, Show, createSignal, useContext, For, type JSX, createResource, Suspense } from 'solid-js';

import { Entity } from '../globals';
import { twclsx, useToast, apiFetch } from '../shared';
import Loading from '../shared/Loading';

type BreadCrumbsProviderProps = {
  children?: JSX.Element;
  class?: string;
};

const makeBreadCrumbs = () => {
  const [breadCrumbs, setBreadCrumbs] = createSignal<
    {
      label: string;
      href?: string;
    }[]
  >([]);

  return {
    breadCrumbs,
    setBreadCrumbs,
  };
};

type BreadCrumbContextType = ReturnType<typeof makeBreadCrumbs>;

const BreadCrumbContext = createContext<BreadCrumbContextType>();

export const useBreadCrumbs = () => {
  const breadCrumb = useContext(BreadCrumbContext);
  if (!breadCrumb) {
    throw new Error('useBreadCrumbs must be used within a BreadCrumbsProvider');
  }

  return breadCrumb;
};

const dataCache = new Map();

const BreadCrumbsProvider = (props: BreadCrumbsProviderProps) => {
  const { breadCrumbs, setBreadCrumbs } = makeBreadCrumbs();
  const params = useParams();
  const { apiPromise } = useToast()!;

  const fetchEntity = async (entityId: string) => {
    // TODO: it should clear cache after update to entity
    const cachedData = dataCache.get(entityId);
    if (cachedData) {
      return cachedData;
    }

    dataCache.set(entityId, (await apiPromise(apiFetch(`/entities/${entityId}`), "Couldn't fetch entity")) as Entity);

    return dataCache.get(entityId);
  };

  const [entity] = createResource(params.entityId, fetchEntity);

  return (
    <BreadCrumbContext.Provider
      value={{
        breadCrumbs,
        setBreadCrumbs,
      }}
    >
      <nav
        class={twclsx(
          'flex',
          'px-5',
          'py-3',
          'text-gray-700',
          'border-y',
          'border-gray-200',
          'bg-bgprimary',
          'dark:bg-gray-800',
          'dark:border-gray-700',
          'fixed',
          'z-20',
          'w-full',
          props.class,
        )}
        aria-label="Breadcrumb"
      >
        <ol class={twclsx('inline-flex', 'items-center', 'space-x-1', 'md:space-x-2', 'rtl:space-x-reverse')}>
          <li class={twclsx('inline-flex', 'items-center')}>
            <a
              href={`/entities/${params.entityId}`}
              class={twclsx(
                'inline-flex',
                'items-center',
                'hover:text-primary-600',
                'dark:hover:text-white',
                'text-sm',
                'font-medium',
                'text-gray-700',
                'dark:text-gray-400',
              )}
            >
              <svg
                class={twclsx('w-3', 'h-3', 'me-2.5')}
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="m19.707 9.293-2-2-7-7a1 1 0 0 0-1.414 0l-7 7-2 2a1 1 0 0 0 1.414 1.414L2 10.414V18a2 2 0 0 0 2 2h3a1 1 0 0 0 1-1v-4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v4a1 1 0 0 0 1 1h3a2 2 0 0 0 2-2v-7.586l.293.293a1 1 0 0 0 1.414-1.414Z" />
              </svg>
              <Suspense fallback={<Loading />}>
                <span class={twclsx('capitalize')}>{entity()?.name}</span>
              </Suspense>
            </a>
          </li>
          <For each={breadCrumbs()}>
            {bc => (
              <li>
                <div class={twclsx('flex', 'items-center')}>
                  <svg
                    class={twclsx('rtl:rotate-180', 'block', 'w-3', 'h-3', 'mx-1', 'text-gray-400')}
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 6 10"
                  >
                    <path
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="m1 9 4-4-4-4"
                    />
                  </svg>
                  <Show when={bc.href}>
                    <A
                      href={bc.href ? bc.href : '#'}
                      class={twclsx(
                        'ms-1',
                        'hover:text-primary-600',
                        'md:ms-2',
                        'dark:hover:text-white',
                        'text-sm',
                        'font-medium',
                        'text-gray-700',
                        'dark:text-gray-400',
                      )}
                    >
                      {bc.label}
                    </A>
                  </Show>
                  <Show when={!bc.href}>
                    <span
                      class={twclsx(
                        'ms-1',
                        'hover:text-primary-600',
                        'md:ms-2',
                        'dark:hover:text-white',
                        'text-sm',
                        'font-medium',
                        'text-gray-700',
                        'dark:text-gray-400',
                      )}
                    >
                      {bc.label}
                    </span>
                  </Show>
                </div>
              </li>
            )}
          </For>
        </ol>
      </nav>

      <span class={twclsx('capitalize', 'text-xl', 'font-semibold', 'text-gray-900', 'sm:text-2xl', 'dark:text-white', 'hidden', 'print:block')}>
        {entity()?.name}
      </span>

      {props.children}
    </BreadCrumbContext.Provider>
  );
};

export default BreadCrumbsProvider;
